import { createFileRoute } from "@tanstack/react-router";
import { IsolatedFlag } from "@/components/system/IsolatedFlag";
import { useEffect } from "react";
import { Sparkles } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { ShowcaseStage } from "@/components/showcase/ShowcaseStage";
import { ShowcaseControls } from "@/components/showcase/ShowcaseControls";
import { useShowcaseMode, useShowcaseUrlBootstrap, showcase } from "@/lib/showcase/showcaseMode";
import { WorldStatusBanner } from "@/components/global/WorldStatusBanner";
import { GlobalEnergyBar } from "@/components/global/GlobalEnergyBar";
import { NationRivalryWidget } from "@/components/social/NationRivalryWidget";
import { TenantWarsWidget } from "@/components/social/TenantWarsWidget";
import { CommunityMoments } from "@/components/social/CommunityMoments";
import { FomoTicker } from "@/components/social/FomoTicker";
import { CinematicMomentsStream } from "@/components/cinematic/CinematicMomentsStream";
import { InfluencerTakeoverBanner } from "@/components/influencer/InfluencerTakeoverBanner";
import { CreatorCameraFrame } from "@/components/creator/CreatorCameraFrame";
import { LiveReactionMoments } from "@/components/social/LiveReactionMoments";
import { useDemoNudge } from "@/lib/challenges/viralChallenges";
import { CountdownPulse } from "@/components/countdown/CountdownPulse";
import { useDemoCountdownPhase } from "@/lib/countdown/worldCupCountdown";
import { useTakeover } from "@/lib/influencer/influencerTakeover";
import { CommunityEnergyTicker } from "@/components/ambassadors/CommunityEnergyTicker";
import { usePersistedCreatorSource } from "@/lib/ambassadors/creatorLinks";
import { SponsorDemoStrip } from "@/components/sponsor/SponsorDemoStrip";
import { useState } from "react";
import { CinematicBrandIntro } from "@/components/brand/CinematicBrandIntro";
import { WorldCupTagline } from "@/components/brand/WorldCupTagline";
import { BrandSlogan } from "@/components/brand/BrandSlogan";
import { WorldPulseLive } from "@/components/showcase/WorldPulseLive";
import { NationLeaderboard } from "@/components/showcase/NationLeaderboard";
import { TrendingNow } from "@/components/showcase/TrendingNow";
import { GlobalHeatMap } from "@/components/showcase/GlobalHeatMap";
import { FomoLayer } from "@/components/showcase/FomoLayer";
import { WorldCupMissions } from "@/components/missions/WorldCupMissions";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/showcase")({
  head: () => ({
    meta: [
      { title: "VerseSport Showcase — Digital World Stadium" },
      { name: "description", content: "Cinematic World Cup showcase: live hype, nation wars, cinematic moments and viral cards in one curated experience." },
      { property: "og:title", content: "VerseSport Showcase — Digital World Stadium" },
      { property: "og:description", content: "Cinematic World Cup showcase: live hype, nation wars, cinematic moments and viral cards in one curated experience." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://versesport.com/showcase" },
      { property: "og:image", content: "https://versesport.com/og/mundial-2026.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "VerseSport Showcase — Digital World Stadium" },
      { name: "twitter:description", content: "Cinematic World Cup showcase: live hype, nation wars, cinematic moments and viral cards in one curated experience." },
      { name: "twitter:image", content: "https://versesport.com/og/mundial-2026.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://versesport.com/showcase" }],
  }),
  component: ShowcasePage,
});

function ShowcasePage() {
  const { t } = useLanguage();
  const s = useShowcaseMode();
  useShowcaseUrlBootstrap();
  usePersistedCreatorSource();
  const phase = useDemoCountdownPhase();
  const takeover = useTakeover();
  useDemoNudge("hype_dom", 9_000);
  const [sponsorMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("mode") === "sponsor";
  });

  // Auto-start on first visit so a cold viewer sees WOW within seconds.
  useEffect(() => {
    if (!s.active) {
      const id = window.setTimeout(() => showcase.start(), 600);
      return () => window.clearTimeout(id);
    }
  }, [s.active]);

  return (
    <main
      className={`mx-auto max-w-3xl space-y-5 px-4 py-6 ${s.creatorMode ? "vs-creator-mode" : ""}`}
      data-showcase="active"
    >
      <CinematicBrandIntro
        sessionKey="vs:cinematic_intro_showcase"
        force={s.creatorMode || sponsorMode}
      />
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-black tracking-tight">{t("sc_title")}</h1>
          <CountdownPulse phase={phase} />
        </div>
        <FomoTicker compact />
      </header>

      <p className="text-sm text-muted-foreground">{t("sc_intro")}</p>
      <BrandSlogan section="splash" size="xs" align="left" />
      <WorldCupTagline size="xs" align="left" withProduct />

      <FomoLayer />

      <InfluencerTakeoverBanner />
      {sponsorMode && <SponsorDemoStrip />}

      {/* P0.1 — Hero dual CTA (above the fold) */}
      <section
        aria-label="Acciones principales"
        className="grid grid-cols-2 gap-2"
      >
        <Link
          to="/nation-wars"
          className="rounded-2xl bg-gradient-to-r from-fuchsia-500 to-indigo-500 px-4 py-3 text-center text-sm font-black uppercase tracking-wider text-white shadow-lg transition hover:scale-[1.02]"
        >
          Elegir mi nación
        </Link>
        <Link
          to="/creator-studio"
          className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-3 text-center text-sm font-black uppercase tracking-wider text-black shadow-lg transition hover:scale-[1.02]"
        >
          Aplicar como Creator
        </Link>
      </section>

      {/* SHOWCASE STAGE — promovido al tope (centro visual) */}
      <section
        aria-label="Showcase reproductor"
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[oklch(0.12_0.05_280)] to-[oklch(0.08_0.02_290)] p-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,.7)]"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fuchsia-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-fuchsia-500" />
            </span>
            <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-white/90">
              La comunidad mundial explota
            </h2>
          </div>
          <ShowcaseControls />
        </div>
        <IsolatedFlag name="ShowcaseStage" flag="cinematic"><ShowcaseStage /></IsolatedFlag>
      </section>

      <CreatorCameraFrame
        lowerThird={
          takeover.active ? (
            <div className="flex items-center gap-2">
              <span className="text-base">{takeover.emoji}</span>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Live takeover
                </div>
                <div className="text-xs font-black">{takeover.name}</div>
              </div>
            </div>
          ) : null
        }
      >
      <IsolatedFlag name="WorldStatusBanner" flag="overlays"><WorldStatusBanner /></IsolatedFlag>
      <IsolatedFlag name="GlobalEnergyBar" flag="overlays"><GlobalEnergyBar /></IsolatedFlag>

      {/* P0.5 — Orden: Pulse → Misiones → Leaderboard → TrendingNow → Momentos → Resto */}
      <WorldPulseLive />

      <WorldCupMissions />

      <NationLeaderboard />

      {/* Creator CTA — visible, sin lógica nueva */}
      <section className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-indigo-500/15 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-300">
              Programa Creator Oficial
            </div>
            <h3 className="mt-1 text-lg font-black leading-tight text-white">
              ¿Quieres ser Creator Oficial de tu país?
            </h3>
            <p className="mt-1 text-xs text-white/70">
              Representa tu nación en VerseSport durante el Mundial. Acceso anticipado, tarjetas exclusivas y boost de visibilidad.
            </p>
          </div>
          <a
            href="/creator-studio"
            className="shrink-0 rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 px-5 py-2.5 text-sm font-black uppercase tracking-wider text-black shadow-lg transition hover:scale-105"
          >
            Aplicar ahora
          </a>
        </div>
      </section>

      <TrendingNow />

      <GlobalHeatMap />

      {/* Ambient real signals — used in case curated beat is paused. */}
      {!s.active && <IsolatedFlag name="CinematicMomentsStream" flag="cinematic"><CinematicMomentsStream /></IsolatedFlag>}

      <LiveReactionMoments />

      <CommunityMoments />

      <div className="grid gap-4 md:grid-cols-2">
        <NationRivalryWidget />
        <TenantWarsWidget />
      </div>

      <div className="flex justify-center">
        <CommunityEnergyTicker />
      </div>
      </CreatorCameraFrame>
    </main>
  );
}
